import { useState, useEffect, useRef } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import {
  AlertTriangle,
  ArrowUpDown,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Receipt,
  Calendar,
  Eye,
  ExternalLink,
  Filter,
  FileText,
  Loader2,
  X,
  Download,
  Search,
  Trash2,
  Plus,
  CloudUpload,
  Link2,
  Upload,
  FileImage,
  ScanLine,
  XCircle,
} from "lucide-react";
import { useScanInvoice } from "../../hooks/useScanInvoice";
import {
  fetchOriginalInvoiceFile,
  downloadInvoicesBatch,
  fetchProcessedInvoices,
  fetchSuppliers,
  fetchStorageIntegrations,
  exportInvoiceToStorage,
  type StorageIntegration,
  type InvoiceFileLink,
  recheckInvoice,
  dismissDuplicateFile,
  resolveDuplicate,
  createInvoiceLine,
  updateInvoiceFields,
  updateInvoiceLineFields,
  deleteInvoice,
  deleteInvoiceLine,
  fetchBusinessDetails,
} from "../../services/apiService";
import type { PaginatedInvoicesResponse } from "../../services/apiService";
import { useLocation, useNavigate } from "react-router-dom";
import { Invoice, Supplier } from "../../types";
import { getCategoriesForIndustry } from "../../data/categories";
import { useBusiness } from "../../contexts/BusinessContext";

// ── Date filter helpers ───────────────────────────────────────────────────────

type DateRange = "all" | "day" | "week" | "month" | "custom";
type SortRule = "uploaded_newest" | "invoice_date_newest" | "amount_high" | "supplier_az";

function getDateStart(range: DateRange): Date | null {
  if (range === "all" || range === "custom") return null;
  const now = new Date();
  if (range === "day") { const d = new Date(now); d.setHours(0, 0, 0, 0); return d; }
  if (range === "week") { const d = new Date(now); d.setDate(now.getDate() - 7); return d; }
  const d = new Date(now); d.setDate(now.getDate() - 30); return d;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const NAVY = "#2a2420";
const ORANGE = "#607840";
const SURFACE = "#fff";
const RAISED = "#fff";
const CANVAS = "#fff";
const LINE = "#e9e1d4";
const MUTED = "#8a8074";
const CHIP = "#f3ece1";
const SHADOW = "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";
const ACCENT_SHADOW = "0 2px 8px rgba(96,120,64,0.25)";
const UP = "#c2412e";
const UP_WEAK = "rgba(194,65,46,0.12)";
const DOWN = "#3c7d57";
const DOWN_WEAK = "rgba(60,125,87,0.12)";
const WARN = "#a07428";
const WARN_WEAK = "rgba(160,116,40,0.12)";
const INVOICE_LIST_COLUMNS = "44px minmax(130px,.75fr) minmax(220px,1.25fr) minmax(110px,.65fr) minmax(130px,.75fr) minmax(120px,.7fr) minmax(80px,.45fr) minmax(100px,.55fr) 44px";
const INVOICE_PAGE_SIZE = 50;

const DATE_FILTERS: { value: DateRange; label: string }[] = [
  { value: "all", label: "All" },
  { value: "day", label: "Today" },
  { value: "week", label: "7d" },
  { value: "month", label: "30d" },
  { value: "custom", label: "Custom" },
];

const SORT_OPTIONS: { value: SortRule; label: string }[] = [
  { value: "uploaded_newest", label: "Newest upload" },
  { value: "invoice_date_newest", label: "Newest invoice date" },
  { value: "amount_high", label: "Highest amount" },
  { value: "supplier_az", label: "Supplier A-Z" },
];


function Highlight({ text, query }: { text: string | null | undefined; query: string }) {
  const str = text ?? "";
  if (!query || !str) return <span style={{ color: NAVY, opacity: str ? 0.65 : 0.35 }}>{str || "—"}</span>;
  const idx = str.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span style={{ color: NAVY, opacity: 0.65 }}>{str}</span>;
  return (
    <span style={{ color: NAVY, opacity: 0.65 }}>
      {str.slice(0, idx)}
      <mark style={{ backgroundColor: `${ORANGE}33`, color: ORANGE, borderRadius: 2, padding: "0 1px", fontWeight: 700 }}>
        {str.slice(idx, idx + query.length)}
      </mark>
      {str.slice(idx + query.length)}
    </span>
  );
}

function displayCurrency(value: number | null | undefined) {
  return value === null || value === undefined ? "null" : `$${value.toLocaleString()}`;
}

function displayDateTime(value: string | null | undefined) {
  if (!value) return "null";
  return new Date(value).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function displayInvoiceDate(value: string | null | undefined) {
  if (!value) return "null";
  const date = parseIsoDate(value);
  if (!date) return value;
  return date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function localIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoDate(date: string) {
  if (!date) return null;
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function monthLabel(date: Date) {
  return date.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
}

function buildCalendarDays(monthDate: Date) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - offset);

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return {
      date: day,
      iso: localIsoDate(day),
      inMonth: day.getMonth() === month,
    };
  });
}

function buildCalendarWeeks(monthDate: Date) {
  const days = buildCalendarDays(monthDate);
  const weeks = Array.from({ length: 6 }, (_, index) => days.slice(index * 7, index * 7 + 7));
  return weeks.filter((week) => week.some((day) => day.inMonth));
}

function DateRangeCalendar({
  start,
  end,
  onChange,
}: {
  start: string;
  end: string;
  onChange: (next: { start: string; end: string }) => void;
}) {
  const initialMonth = parseIsoDate(start) ?? new Date();
  const [viewMonth, setViewMonth] = useState(() => new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));
  const todayIso = localIsoDate(new Date());
  const [selectingEnd, setSelectingEnd] = useState(Boolean(start && !end));
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const previewEnd = selectingEnd ? hoverDate : null;
  const rangeEnd = end || previewEnd || "";

  function handleDateClick(iso: string) {
    if (!start || (start && end) || iso < start) {
      onChange({ start: iso, end: "" });
      setSelectingEnd(true);
      setHoverDate(null);
      return;
    }

    onChange({ start, end: iso });
    setSelectingEnd(false);
    setHoverDate(null);
  }

  function dayState(iso: string) {
    const isStart = iso === start;
    const isEnd = Boolean(end && iso === end);
    const isPreviewEnd = Boolean(!end && previewEnd && iso === previewEnd && iso !== start);
    const inRange = Boolean(start && rangeEnd && iso > start && iso < rangeEnd);
    const isToday = iso === todayIso;
    return { isStart, isEnd, isPreviewEnd, inRange, isToday };
  }

  function CalendarMonth({ month }: { month: Date }) {
    return (
      <div className="w-[260px] flex-shrink-0">
        <p className="text-center text-xs font-bold" style={{ color: NAVY }}>{monthLabel(month)}</p>
        <div className="mt-3 grid grid-cols-7 gap-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="h-7 text-center text-[10px] font-bold leading-7" style={{ color: NAVY, opacity: 0.35 }}>
              {day}
            </div>
          ))}
          {buildCalendarWeeks(month).flat().map(({ date, iso, inMonth }) => {
            if (!inMonth) return <div key={iso} className="h-8" />;

            const { isStart, isEnd, isPreviewEnd, inRange, isToday } = dayState(iso);
            const selected = isStart || isEnd || isPreviewEnd;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => handleDateClick(iso)}
                onMouseEnter={() => {
                  if (selectingEnd && start && iso >= start) setHoverDate(iso);
                }}
                className="h-8 rounded-lg text-xs font-semibold transition-colors"
                style={{
                  color: selected ? "white" : NAVY,
                  backgroundColor: selected
                    ? ORANGE
                    : inRange
                      ? ACCENT_WEAK
                      : "transparent",
                  border: isStart && selectingEnd && !end
                    ? `1px solid ${ORANGE}`
                    : isToday && !selected
                      ? `1px solid ${ORANGE}`
                      : "1px solid transparent",
                }}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div onMouseLeave={() => setHoverDate(null)}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setViewMonth((month) => addMonths(month, -1))}
          className="flex h-8 w-8 items-center justify-center rounded-lg"
          style={{ color: NAVY, backgroundColor: `${NAVY}08` }}
          title="Previous month"
        >
          <ChevronLeft size={15} />
        </button>
        <div />
        <button
          type="button"
          onClick={() => setViewMonth((month) => addMonths(month, 1))}
          className="flex h-8 w-8 items-center justify-center rounded-lg"
          style={{ color: NAVY, backgroundColor: `${NAVY}08` }}
          title="Next month"
        >
          <ChevronRight size={15} />
        </button>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <CalendarMonth month={viewMonth} />
        <CalendarMonth month={addMonths(viewMonth, 1)} />
      </div>
    </div>
  );
}

function invoiceDateQueryParams(
  range: DateRange,
  customStart: string,
  customEnd: string,
) {
  if (range === "custom") {
    return { dateStart: customStart || undefined, dateEnd: customEnd || undefined };
  }

  const start = getDateStart(range);
  return { dateStart: start ? localIsoDate(start) : undefined, dateEnd: undefined };
}

function rangeLabel(page: number, pageSize: number, total: number) {
  if (total === 0) return "0 invoices";
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return `${start}-${end} of ${total} invoice${total !== 1 ? "s" : ""}`;
}

function formatNumber(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

type LineCorrection = {
  productCode?: string;
  description?: string;
  quantity?: string | number;
  unit?: string;
  unitPrice?: string | number;
  totalPrice?: string | number;
};

type InvoiceCorrection = {
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  totalAmount?: string | number;
};

type DeleteTarget =
  | { type: "invoice"; id: string; title: string; body: string }
  | { type: "line"; id: string; title: string; body: string };

function DeleteConfirmModal({
  target,
  isDeleting,
  error,
  showExternalStorageWarning = false,
  onCancel,
  onDelete,
}: {
  target: DeleteTarget;
  isDeleting: boolean;
  error: string;
  showExternalStorageWarning?: boolean;
  onCancel: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(15,23,42,0.48)" }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-lg p-5"
        style={{ backgroundColor: SURFACE, boxShadow: "0 18px 50px rgba(0,0,0,0.24)", border: "1px solid rgba(220,38,38,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(220,38,38,0.08)" }}>
            <AlertTriangle size={17} style={{ color: "#dc2626" }} />
          </div>
          <div>
            <h2 className="text-sm font-extrabold" style={{ color: NAVY }}>{target.title}</h2>
            <p className="text-xs mt-1 leading-relaxed" style={{ color: NAVY, opacity: 0.62 }}>{target.body}</p>
            <p className="text-xs mt-2 font-semibold" style={{ color: UP }}>This action can't be undone.</p>
            {showExternalStorageWarning && (
              <p className="text-xs mt-2 font-semibold" style={{ color: UP }}>
                This will also delete exported copies of this invoice from connected external storage.
              </p>
            )}
          </div>
        </div>

        {error && (
          <p className="text-xs mt-4 rounded-md px-3 py-2" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)" }}>
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="rounded-md px-4 py-2 text-xs font-bold transition-all disabled:opacity-50"
            style={{ color: NAVY, backgroundColor: `${NAVY}0d`, border: `1px solid ${NAVY}1a` }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={isDeleting}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white transition-all disabled:opacity-50"
            style={{ backgroundColor: "#dc2626", boxShadow: "0 2px 8px rgba(220,38,38,0.25)" }}
          >
            {isDeleting && <Loader2 size={13} className="animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function FormInput({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "number" | "date" | "email";
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="space-y-1">
      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.5 }}>
        {label}{required ? " *" : ""}
      </span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md px-3 py-2 text-xs outline-none transition-all"
        style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
      />
    </label>
  );
}

function CategoryDropdown({
  value,
  onChange,
  categories,
}: {
  value: string;
  onChange: (value: string) => void;
  categories: string[];
}) {
  return (
    <label className="space-y-1">
      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.5 }}>
        Category
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md px-3 py-2 text-xs outline-none transition-all"
        style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
      >
        {categories.map((category) => (
          <option key={category} value={category}>{category}</option>
        ))}
      </select>
    </label>
  );
}

type InvoiceForm = {
  supplierId: string;
  supplierMode: "existing" | "new";
  supplierName: string;
  supplierEmail: string;
  supplierPhone: string;
  supplierAddress: string;
  supplierTaxNumber: string;
  supplierCategory: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  subtotal: string;
  taxAmount: string;
  totalAmount: string;
  currency: string;
  lineItems: LineForm[];
};

const EMPTY_INVOICE_FORM: InvoiceForm = {
  supplierId: "",
  supplierMode: "existing",
  supplierName: "",
  supplierEmail: "",
  supplierPhone: "",
  supplierAddress: "",
  supplierTaxNumber: "",
  supplierCategory: "Other",
  invoiceNumber: "",
  invoiceDate: "",
  dueDate: "",
  subtotal: "",
  taxAmount: "",
  totalAmount: "",
  currency: "USD",
  lineItems: [],
};

const EMPTY_LINE_FORM: LineForm = {
  productCode: "",
  description: "",
  quantity: "",
  unit: "",
  unitPrice: "",
  totalPrice: "",
  category: "Other",
};

const MUTED_SCAN = MUTED;

function ScanModal({ onClose }: { onClose: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const { step, progress, session, error, scanInvoices, reset } = useScanInvoice();

  const isIdle = step === "idle";
  const isComplete = step === "complete";
  const isError = step === "error";
  const isRunning = !isIdle && !isComplete && !isError;
  const canScan = files.length > 0 && isIdle;

  const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
    accept: { "image/*": [".jpg", ".jpeg", ".png", ".webp"], "application/pdf": [".pdf"] },
    maxFiles: 20,
    multiple: true,
    disabled: !isIdle,
    onDrop: (accepted) => {
      if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted].slice(0, 20));
    },
  });

  function handleScan() {
    if (canScan) scanInvoices(files);
  }

  function handleReset() {
    setFiles([]);
    reset();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget && !isRunning) onClose(); }}
    >
      <div className="relative w-full max-w-md mx-4 rounded-2xl p-7" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: "0 24px 56px rgba(0,0,0,0.14)" }}>
        {!isRunning && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 flex items-center justify-center w-7 h-7 rounded-lg transition-colors"
            style={{ color: MUTED_SCAN, border: `1px solid ${LINE}` }}
          >
            <X size={13} />
          </button>
        )}

        {isComplete ? (
          <div className="flex flex-col items-center text-center gap-5 py-2">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: DOWN_WEAK }}>
              <CheckCircle size={34} style={{ color: DOWN }} />
            </div>
            <div>
              <p className="text-base font-extrabold" style={{ color: NAVY }}>
                {session?.invoiceCount === 1 ? "Invoice submitted!" : `${session?.invoiceCount} invoices submitted!`}
              </p>
              <p className="text-xs mt-1.5 leading-relaxed" style={{ color: MUTED_SCAN }}>
                AI is extracting supplier, products, and totals in the background. Check the Invoices page shortly.
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={handleReset}
                className="flex-1 py-2.5 text-sm font-semibold rounded-xl transition-all"
                style={{ border: `1px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
              >
                Scan another
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-2.5 text-sm font-bold text-white rounded-xl"
                style={{ backgroundColor: ORANGE }}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <h2 className="text-sm font-extrabold" style={{ color: NAVY }}>Scan Invoice</h2>
              <p className="text-xs mt-0.5" style={{ color: MUTED_SCAN }}>Upload invoice files to be extracted by AI.</p>
            </div>

            <div
              {...getRootProps()}
              className="outline-none cursor-pointer transition-all duration-200"
              style={{
                minHeight: 180,
                borderRadius: 16,
                border: isDragActive ? `2px dashed ${ORANGE}` : `1px dashed ${LINE}`,
                backgroundColor: isDragActive ? ACCENT_WEAK : RAISED,
                opacity: !isIdle ? 0.5 : 1,
                pointerEvents: !isIdle ? "none" : undefined,
              }}
            >
              <input {...getInputProps()} />
              {files.length > 0 ? (
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold" style={{ color: NAVY }}>{files.length} file{files.length !== 1 ? "s" : ""} selected</p>
                    {isIdle && (
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={(e) => { e.stopPropagation(); openFilePicker(); }} className="text-xs font-semibold" style={{ color: ORANGE }}>
                          + Add more
                        </button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setFiles([]); }} className="text-xs" style={{ color: MUTED_SCAN }}>
                          Remove all
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="max-h-[160px] overflow-y-auto space-y-1.5">
                    {files.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="flex items-center gap-2.5 px-3 py-2 rounded-xl" style={{ border: `1px solid ${LINE}` }}>
                        <FileImage size={13} style={{ color: MUTED_SCAN, flexShrink: 0 }} />
                        <span className="text-xs truncate flex-1" style={{ color: NAVY }}>{f.name}</span>
                        {isIdle && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); setFiles((prev) => prev.filter((_, idx) => idx !== i)); }}>
                            <X size={11} style={{ color: MUTED_SCAN }} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 py-10">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ border: `1px solid ${LINE}` }}>
                    <Upload size={20} style={{ color: ORANGE }} />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold" style={{ color: NAVY }}>{isDragActive ? "Drop here" : "Drag & drop invoices"}</p>
                    <p className="text-xs mt-0.5" style={{ color: MUTED_SCAN }}>JPG, PNG, WEBP, PDF · Max 10 MB · Up to 20</p>
                  </div>
                  <span className="text-xs px-3 py-1 rounded-full" style={{ color: MUTED_SCAN, border: `1px solid ${LINE}` }}>or click to browse</span>
                </div>
              )}
            </div>

            {isRunning && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Loader2 size={13} className="animate-spin" style={{ color: ORANGE }} />
                  <span className="text-xs font-semibold" style={{ color: NAVY }}>{session?.currentStep ?? "Processing..."}</span>
                </div>
                <div className="w-full rounded-full h-1.5 overflow-hidden" style={{ backgroundColor: CHIP }}>
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progress}%`, backgroundColor: ORANGE }} />
                </div>
              </div>
            )}

            {isError && error && (
              <div className="mt-4 flex items-center gap-2.5 px-4 py-3 rounded-xl" style={{ backgroundColor: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)" }}>
                <XCircle size={14} style={{ color: "#dc2626", flexShrink: 0 }} />
                <p className="text-xs" style={{ color: "#991b1b" }}>{error}</p>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              {(isError) && (
                <button onClick={handleReset} className="px-4 py-2 text-xs font-semibold rounded-xl" style={{ border: `1px solid ${LINE}`, color: NAVY }}>
                  Try again
                </button>
              )}
              <button
                onClick={handleScan}
                disabled={!canScan}
                className="px-5 py-2.5 text-sm font-bold text-white rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                style={{ backgroundColor: ORANGE }}
              >
                {isRunning ? "Scanning…" : `Scan ${files.length > 1 ? `${files.length} Invoices` : "Invoice"}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CreateInvoiceModal({
  suppliers,
  isSaving,
  error,
  onClose,
  onSubmit,
  categories,
}: {
  suppliers: Supplier[];
  isSaving: boolean;
  error: string;
  onClose: () => void;
  onSubmit: (form: InvoiceForm) => void;
  categories: string[];
}) {
  const [form, setForm] = useState<InvoiceForm>(() => ({
    ...EMPTY_INVOICE_FORM,
    supplierId: suppliers[0]?.id || "",
    lineItems: [{ ...EMPTY_LINE_FORM, category: suppliers[0]?.category || "Other" }],
  }));
  const selectedSupplier = suppliers.find((supplier) => supplier.id === form.supplierId);
  const usingExisting = form.supplierMode === "existing";

  function update<K extends keyof InvoiceForm>(key: K, value: InvoiceForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function chooseSupplier(id: string) {
    const supplier = suppliers.find((item) => item.id === id);
    setForm((prev) => ({
      ...prev,
      supplierId: id,
      lineItems: prev.lineItems.map((line) => ({
        ...line,
        category: line.category === "Other" ? supplier?.category || line.category : line.category,
      })),
    }));
  }

  function updateLine(index: number, key: keyof LineForm, value: string) {
    setForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((line, lineIndex) => (
        lineIndex === index ? { ...line, [key]: value } : line
      )),
    }));
  }

  function addLine() {
    const fallbackCategory = usingExisting ? selectedSupplier?.category : form.supplierCategory;
    setForm((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, { ...EMPTY_LINE_FORM, category: fallbackCategory || "Other" }],
    }));
  }

  function removeLine(index: number) {
    setForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, lineIndex) => lineIndex !== index),
    }));
  }

  const supplierPreview = usingExisting && selectedSupplier;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(15,23,42,0.52)" }} onClick={onClose}>
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg"
        style={{ backgroundColor: SURFACE, boxShadow: "0 18px 60px rgba(0,0,0,0.28)", border: `1px solid ${LINE}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${NAVY}14` }}>
          <div>
            <h2 className="text-sm font-extrabold" style={{ color: NAVY }}>Create invoice</h2>
            <p className="text-xs mt-0.5" style={{ color: NAVY, opacity: 0.55 }}>Add a manual invoice to this business.</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-md flex items-center justify-center" style={{ color: NAVY, backgroundColor: `${NAVY}0d` }}>
            <X size={15} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex gap-2">
            {(["existing", "new"] as const).map((mode) => {
              const active = form.supplierMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => update("supplierMode", mode)}
                  className="rounded-md px-3 py-2 text-xs font-bold transition-all"
                  style={active ? { backgroundColor: ORANGE, color: "white" } : { color: NAVY, backgroundColor: `${NAVY}0d` }}
                >
                  {mode === "existing" ? "Choose supplier" : "Add new supplier"}
                </button>
              );
            })}
          </div>

          {usingExisting ? (
            <div className="space-y-3">
              <label className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.5 }}>Supplier *</span>
                <select
                  value={form.supplierId}
                  onChange={(e) => chooseSupplier(e.target.value)}
                  className="w-full rounded-md px-3 py-2 text-xs outline-none transition-all"
                  style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                >
                  <option value="">Select supplier</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
              </label>
              {supplierPreview && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 rounded-lg p-3" style={{ backgroundColor: RAISED, border: `1px solid ${LINE}` }}>
                  {[
                    ["Name", supplierPreview.name],
                    ["Category", supplierPreview.category],
                    ["Email", supplierPreview.email || "Not set"],
                    ["Phone", supplierPreview.phone || "Not set"],
                    ["Address", supplierPreview.address || "Not set"],
                    ["Tax number", supplierPreview.taxNumber || "Not set"],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.4 }}>{label}</p>
                      <p className="text-xs mt-1 font-semibold" style={{ color: NAVY }}>{value}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormInput label="Supplier name" required value={form.supplierName} onChange={(v) => update("supplierName", v)} />
              <CategoryDropdown
                categories={categories}
                value={form.supplierCategory}
                onChange={(v) => {
                  update("supplierCategory", v);
                  setForm((prev) => ({
                    ...prev,
                    supplierCategory: v,
                    lineItems: prev.lineItems.map((line) => ({
                      ...line,
                      category: line.category === "Other" ? v : line.category,
                    })),
                  }));
                }}
              />
              <FormInput label="Email" type="email" value={form.supplierEmail} onChange={(v) => update("supplierEmail", v)} />
              <FormInput label="Phone" value={form.supplierPhone} onChange={(v) => update("supplierPhone", v)} />
              <FormInput label="Address" value={form.supplierAddress} onChange={(v) => update("supplierAddress", v)} />
              <FormInput label="Tax number" value={form.supplierTaxNumber} onChange={(v) => update("supplierTaxNumber", v)} />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FormInput label="Invoice number" required value={form.invoiceNumber} onChange={(v) => update("invoiceNumber", v)} />
            <FormInput label="Invoice date" required type="date" value={form.invoiceDate} onChange={(v) => update("invoiceDate", v)} />
            <FormInput label="Due date" type="date" value={form.dueDate} onChange={(v) => update("dueDate", v)} />
            <FormInput label="Subtotal" type="number" value={form.subtotal} onChange={(v) => update("subtotal", v)} />
            <FormInput label="Tax" type="number" value={form.taxAmount} onChange={(v) => update("taxAmount", v)} />
            <FormInput label="Total" required type="number" value={form.totalAmount} onChange={(v) => update("totalAmount", v)} />
            <FormInput label="Currency" required value={form.currency} onChange={(v) => update("currency", v.toUpperCase())} />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.5 }}>Line items</p>
                <p className="text-xs mt-0.5" style={{ color: NAVY, opacity: 0.45 }}>Add products before creating the invoice.</p>
              </div>
              <button
                type="button"
                onClick={addLine}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold text-white"
                style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
              >
                <Plus size={12} />
                Add line
              </button>
            </div>

            <div className="rounded-lg overflow-x-auto" style={{ border: `1px solid ${LINE}` }}>
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
                    {["Code", "Description", "Qty", "Unit", "Unit Price", "Total", "Category", ""].map((header, index) => (
                      <th
                        key={header || "actions"}
                        className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest ${index >= 2 && index <= 5 ? "text-right" : "text-left"}`}
                        style={{ color: NAVY, opacity: 0.5 }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {form.lineItems.map((line, index) => (
                    <tr key={index} style={{ borderTop: index > 0 ? `1px solid ${NAVY}0d` : "none" }}>
                      <td className="px-3 py-2">
                        <input
                          value={line.productCode}
                          onChange={(e) => updateLine(index, "productCode", e.target.value)}
                          className="w-full rounded-md px-2 py-1.5 text-xs outline-none"
                          style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={line.description}
                          onChange={(e) => updateLine(index, "description", e.target.value)}
                          placeholder="Description"
                          className="w-full rounded-md px-2 py-1.5 text-xs outline-none"
                          style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={line.quantity}
                          onChange={(e) => updateLine(index, "quantity", e.target.value)}
                          className="w-full rounded-md px-2 py-1.5 text-xs text-right outline-none"
                          style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          value={line.unit}
                          onChange={(e) => updateLine(index, "unit", e.target.value)}
                          className="w-full rounded-md px-2 py-1.5 text-xs text-right outline-none"
                          style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={line.unitPrice}
                          onChange={(e) => updateLine(index, "unitPrice", e.target.value)}
                          className="w-full rounded-md px-2 py-1.5 text-xs text-right outline-none"
                          style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={line.totalPrice}
                          onChange={(e) => updateLine(index, "totalPrice", e.target.value)}
                          className="w-full rounded-md px-2 py-1.5 text-xs text-right outline-none"
                          style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={line.category}
                          onChange={(e) => updateLine(index, "category", e.target.value)}
                          className="w-full rounded-md px-2 py-1.5 text-xs outline-none"
                          style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                        >
                          {categories.map((category) => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeLine(index)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md"
                          style={{ color: "#dc2626", backgroundColor: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.16)" }}
                          aria-label="Remove line"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {form.lineItems.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-center">
                        <p className="text-xs" style={{ color: NAVY, opacity: 0.45 }}>No line items yet.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {error && (
            <p className="text-xs rounded-md px-3 py-2" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)" }}>
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} disabled={isSaving} className="rounded-md px-4 py-2 text-xs font-bold disabled:opacity-50" style={{ color: NAVY, backgroundColor: `${NAVY}0d`, border: `1px solid ${NAVY}1a` }}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSubmit(form)}
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
            >
              {isSaving && <Loader2 size={13} className="animate-spin" />}
              Create invoice
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



// ── InvoiceRow ────────────────────────────────────────────────────────────────

function EditableCell({
  value,
  type = "text",
  align = "left",
  onSave,
}: {
  value: string | number | null | undefined;
  type?: "text" | "number" | "date";
  align?: "left" | "right" | "center";
  onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  const focusedRef = useRef(false);

  // When value changes externally (e.g. after "Use suggested total" refetches),
  // update the displayed draft — but only if the user isn't currently typing.
  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(value == null ? "" : String(value));
    }
  }, [value]);

  function save() {
    const next = draft.trim();
    if (next !== (value == null ? "" : String(value))) onSave(next);
  }

  return (
    <input
      type={type}
      value={draft}
      placeholder="—"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => { focusedRef.current = true; }}
      onBlur={() => { focusedRef.current = false; save(); }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(value == null ? "" : String(value));
          e.currentTarget.blur();
        }
      }}
      className={`w-full rounded-md px-2 py-1.5 text-xs outline-none transition-all appearance-none ${align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"}`}
      style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
    />
  );
}

type LineForm = {
  productCode: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  totalPrice: string;
  category: string;
};

function AddLineForm({
  defaultCategory,
  isSaving,
  onCancel,
  onSubmit,
  categories,
}: {
  defaultCategory: string;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (form: LineForm) => void;
  categories: string[];
}) {
  const [form, setForm] = useState<LineForm>({
    productCode: "",
    description: "",
    quantity: "",
    unit: "",
    unitPrice: "",
    totalPrice: "",
    category: defaultCategory || "Other",
  });

  function update<K extends keyof LineForm>(key: K, value: LineForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="rounded-lg p-4" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
      <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: NAVY, opacity: 0.45 }}>
        Add invoice line
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <FormInput label="Product code" value={form.productCode} onChange={(v) => update("productCode", v)} />
        <FormInput label="Description" required value={form.description} onChange={(v) => update("description", v)} />
        <FormInput label="Qty" type="number" value={form.quantity} onChange={(v) => update("quantity", v)} />
        <FormInput label="Unit" value={form.unit} onChange={(v) => update("unit", v)} />
        <FormInput label="Unit price" type="number" value={form.unitPrice} onChange={(v) => update("unitPrice", v)} />
        <FormInput label="Total" type="number" value={form.totalPrice} onChange={(v) => update("totalPrice", v)} />
        <CategoryDropdown value={form.category} onChange={(v) => update("category", v)} categories={categories} />
        <div className="flex items-end justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="rounded-md px-3 py-2 text-xs font-bold disabled:opacity-50"
            style={{ color: NAVY, backgroundColor: `${NAVY}0d`, border: `1px solid ${NAVY}1a` }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit(form)}
            disabled={isSaving}
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
          >
            {isSaving && <Loader2 size={13} className="animate-spin" />}
            Add line
          </button>
        </div>
      </div>
    </div>
  );
}

const PROVIDER_LABELS: Record<string, string> = {
  dropbox: "Dropbox",
  google_drive: "Google Drive",
  onedrive: "OneDrive",
};

function StorageExportModal({
  invoiceId,
  businessId,
  onClose,
}: {
  invoiceId: string;
  businessId?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exportResult, setExportResult] = useState<{ provider: string; status: string; path?: string; error?: string }[] | null>(null);
  const [exportError, setExportError] = useState("");

  const { data: integrations, isLoading } = useQuery<StorageIntegration[]>({
    queryKey: ["storage-integrations", businessId],
    queryFn: () => fetchStorageIntegrations(businessId),
  });

  const connected: StorageIntegration[] = (integrations ?? []).filter((i: StorageIntegration) => i.status === "connected" && i.provider !== "onedrive");

  const exportMutation = useMutation({
    mutationFn: () => exportInvoiceToStorage(invoiceId, Array.from(selected), businessId),
    onSuccess: (result) => setExportResult(result),
    onError: (e) => setExportError(e instanceof Error ? e.message : "Export failed"),
  });

  function toggleProvider(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allSelected = connected.length > 0 && selected.size === connected.length;

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(connected.map((i) => i.provider)));
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(15,23,42,0.48)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-xl p-5 space-y-4"
        style={{ backgroundColor: SURFACE, boxShadow: "0 18px 50px rgba(0,0,0,0.22)", border: `1px solid ${LINE}` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${NAVY}0d` }}>
              <CloudUpload size={15} style={{ color: NAVY }} />
            </div>
            <h2 className="text-sm font-extrabold" style={{ color: NAVY }}>Upload to cloud</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md transition-colors" style={{ color: NAVY, opacity: 0.4 }}>
            <X size={15} />
          </button>
        </div>

        {exportResult ? (
          /* ── Result view ── */
          <div className="space-y-2">
            {exportResult.map((d) => (
              <div
                key={d.provider}
                className="flex items-start gap-2.5 rounded-lg px-3 py-2.5"
                style={{
                  backgroundColor: d.status === "success" ? DOWN_WEAK : UP_WEAK,
                  border: d.status === "success" ? `1px solid ${DOWN}33` : `1px solid ${UP}33`,
                }}
              >
                {d.status === "success"
                  ? <CheckCircle size={14} style={{ color: DOWN, flexShrink: 0, marginTop: 1 }} />
                  : <AlertTriangle size={14} style={{ color: UP, flexShrink: 0, marginTop: 1 }} />}
                <div className="min-w-0">
                  <p className="text-xs font-bold" style={{ color: d.status === "success" ? DOWN : UP }}>
                    {PROVIDER_LABELS[d.provider] ?? d.provider}
                  </p>
                  <p className="text-[11px] mt-0.5 break-all" style={{ color: d.status === "success" ? DOWN : UP, opacity: 0.8 }}>
                    {d.status === "success" ? (d.path || "Uploaded") : d.error}
                  </p>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-lg py-2 text-xs font-bold transition-all mt-1"
              style={{ backgroundColor: RAISED, color: NAVY, border: `1px solid ${LINE}` }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* ── Connected integrations ── */}
            {isLoading ? (
              <div className="flex items-center gap-2 py-3 justify-center">
                <Loader2 size={15} className="animate-spin" style={{ color: NAVY, opacity: 0.4 }} />
                <span className="text-xs" style={{ color: NAVY, opacity: 0.45 }}>Loading connections…</span>
              </div>
            ) : connected.length === 0 ? (
              <p className="text-xs text-center py-2" style={{ color: NAVY, opacity: 0.55 }}>
                No storage connections yet.
              </p>
            ) : (
              <div className="space-y-2">
                {connected.length > 1 && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-[11px] font-semibold transition-colors"
                    style={{ color: ORANGE }}
                  >
                    {allSelected ? "Deselect all" : "Select all"}
                  </button>
                )}
                {connected.map((integration) => {
                  const checked = selected.has(integration.provider);
                  return (
                    <label
                      key={integration.provider}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-all"
                      style={{
                        border: `1.5px solid ${checked ? ACCENT_LINE : LINE}`,
                        backgroundColor: checked ? ACCENT_WEAK : SURFACE,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleProvider(integration.provider)}
                        className="rounded"
                        style={{ accentColor: ORANGE }}
                      />
                      <span className="text-xs font-semibold" style={{ color: NAVY }}>
                        {PROVIDER_LABELS[integration.provider] ?? integration.provider}
                      </span>
                      {integration.rootFolderName && (
                        <span className="ml-auto text-[11px] truncate max-w-[100px]" style={{ color: NAVY, opacity: 0.45 }}>
                          {integration.rootFolderName}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}

            {exportError && (
              <p className="text-xs rounded-md px-3 py-2" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)" }}>
                {exportError}
              </p>
            )}

            {/* ── Actions ── */}
            <div className="space-y-2 pt-1">
              <button
                type="button"
                onClick={() => exportMutation.mutate()}
                disabled={selected.size === 0 || exportMutation.isPending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold text-white transition-all disabled:opacity-40"
                style={{ backgroundColor: ORANGE, boxShadow: selected.size > 0 ? ACCENT_SHADOW : undefined }}
              >
                {exportMutation.isPending && <Loader2 size={13} className="animate-spin" />}
                {exportMutation.isPending ? "Uploading…" : `Upload to ${selected.size > 0 ? `${selected.size} destination${selected.size > 1 ? "s" : ""}` : "…"}`}
              </button>
              <button
                type="button"
                onClick={() => navigate("/integrations")}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-semibold transition-all"
                style={{ color: NAVY, opacity: 0.6, backgroundColor: `${NAVY}07`, border: `1px solid ${NAVY}14` }}
              >
                <Link2 size={12} />
                Add connection
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InvoiceRow({
  inv,
  businessId,
  searchQuery = "",
  isNew = false,
  deletesExternalOnInvoiceDelete = false,
  selected = false,
  rowIndex = 0,
  onToggleSelect,
  onOpen,
}: {
  inv: Invoice;
  businessId?: string;
  searchQuery?: string;
  categories: string[];
  isNew?: boolean;
  deletesExternalOnInvoiceDelete?: boolean;
  selected?: boolean;
  rowIndex?: number;
  onToggleSelect: (id: string) => void;
  onSearch?: (query: string) => void;
  onOpen: (id: string) => void;
}) {
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const queryClient = useQueryClient();

  const deleteInvoiceMutation = useMutation({
    mutationFn: () => deleteInvoice(inv.id, businessId),
    onSuccess: () => {
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["invoices", businessId] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-summary", businessId] });
    },
    onError: (error) => setDeleteError(error instanceof Error ? error.message : "Could not delete invoice."),
  });
  const [hovered, setHovered] = useState(false);
  const baseRowBackground = selected ? ACCENT_WEAK : SURFACE;
  const activeBorder = hovered || selected;

  return (
    <>
      {deleteTarget && (
        <DeleteConfirmModal
          target={deleteTarget}
          isDeleting={deleteInvoiceMutation.isPending}
          error={deleteError}
          showExternalStorageWarning={deleteTarget.type === "invoice" && deletesExternalOnInvoiceDelete}
          onCancel={() => setDeleteTarget(null)}
          onDelete={() => deleteInvoiceMutation.mutate()}
        />
      )}
      <div
        role="row"
        tabIndex={0}
        aria-selected={selected}
        className="grid w-full cursor-pointer select-none items-center gap-4 px-5 py-3.5 text-left transition-all"
        style={{
          gridTemplateColumns: INVOICE_LIST_COLUMNS,
          backgroundColor: baseRowBackground,
          border: `1.5px solid ${activeBorder ? ACCENT_LINE : "transparent"}`,
          boxShadow: activeBorder ? "0 7px 18px rgba(96,120,64,0.08)" : "none",
        }}
        onClick={() => onOpen(inv.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(inv.id);
          }
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div role="cell" className="flex justify-center" onClick={(e) => { e.stopPropagation(); onToggleSelect(inv.id); }}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(inv.id)}
            onClick={(e) => e.stopPropagation()}
            className="cursor-pointer align-middle"
            style={{ width: 15, height: 15, accentColor: ORANGE }}
            aria-label={`Select invoice ${inv.invoiceNumber || ""}`}
          />
        </div>
        <div
          role="cell"
          className="relative min-w-0 truncate text-xs font-semibold tabular-nums"
          style={{ minHeight: 24, paddingLeft: (isNew || inv.hasDuplicateFile || inv.status === "duplicate") ? 8 : 0 }}
        >
          {(inv.hasDuplicateFile || inv.status === "duplicate") ? (
            <div style={{ position: "absolute", top: 0, left: 0, width: 40, height: 40, overflow: "hidden", pointerEvents: "none" }}>
              <div style={{ position: "absolute", top: 9, left: -13, width: 52, transform: "rotate(-45deg)", backgroundColor: "#c2410c", color: "white", fontSize: 6, fontWeight: 800, textAlign: "center", letterSpacing: "0.05em", lineHeight: "14px", textTransform: "uppercase" }}>
                DUP
              </div>
            </div>
          ) : isNew ? (
            <div style={{ position: "absolute", top: 0, left: 0, width: 40, height: 40, overflow: "hidden", pointerEvents: "none" }}>
              <div style={{ position: "absolute", top: 9, left: -13, width: 52, transform: "rotate(-45deg)", backgroundColor: ORANGE, color: "white", fontSize: 7, fontWeight: 800, textAlign: "center", letterSpacing: "0.07em", lineHeight: "14px", textTransform: "uppercase" }}>
                NEW
              </div>
            </div>
          ) : null}
          <Highlight text={inv.invoiceNumber} query={searchQuery} />
        </div>
        <div role="cell" className="min-w-0">
          <div className="text-xs font-semibold" style={{ color: NAVY }}>{inv.supplier.name}</div>
          <div className="mt-0.5 text-xs" style={{ color: NAVY, opacity: 0.55 }}>{inv.supplier.category}</div>
        </div>
        <div role="cell" className="whitespace-nowrap text-xs font-medium" style={{ color: NAVY, opacity: 0.58 }}>
          {displayDateTime(inv.uploadedAt || inv.receivedDate)}
        </div>
        <div role="cell" className="whitespace-nowrap text-xs font-medium" style={{ color: NAVY, opacity: 0.58 }}>
          {inv.uploadedBy ?? "—"}
        </div>
        <div role="cell" className="whitespace-nowrap text-xs" style={{ color: NAVY, opacity: 0.62 }}>
          {displayInvoiceDate(inv.invoiceDate)}
        </div>
        <div role="cell" className="text-right text-xs" style={{ color: NAVY, opacity: 0.7 }}>
          {displayCurrency(inv.taxAmount)}
        </div>
        <div role="cell" className="text-center text-xs font-extrabold tabular-nums" style={{ color: NAVY }}>
          {inv.totalAmount == null ? "null" : String(inv.totalAmount)}
        </div>
        <div role="cell">
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteError("");
                setDeleteTarget({ type: "invoice", id: inv.id, title: "Delete invoice?", body: `Delete invoice ${inv.invoiceNumber || "without a number"} and all of its line items.` });
              }}
              className="w-7 h-7 rounded-md flex items-center justify-center transition-colors hover:bg-black/5"
              style={{ color: "#dc2626" }}
              aria-label="Delete invoice"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── InvoiceSlidingPanel ───────────────────────────────────────────────────────

export function InvoiceSlidingPanel({
  inv,
  businessId,
  categories,
  deletesExternalOnInvoiceDelete = false,
  onClose,
  onSearch,
  highlightProductCode,
}: {
  inv: Invoice;
  businessId?: string;
  categories: string[];
  deletesExternalOnInvoiceDelete?: boolean;
  onClose: () => void;
  onSearch?: (query: string) => void;
  highlightProductCode?: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [recheckMessage, setRecheckMessage] = useState("");
  const [previewFile, setPreviewFile] = useState<InvoiceFileLink | null>(null);
  const [originalFile, setOriginalFile] = useState<InvoiceFileLink | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [externalLoading, setExternalLoading] = useState(false);
  const [externalMenuOpen, setExternalMenuOpen] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [externalStorageMessage, setExternalStorageMessage] = useState("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [addingLine, setAddingLine] = useState(false);
  const [lineCreateError, setLineCreateError] = useState("");
  const [reviewDupOpen, setReviewDupOpen] = useState(false);
  const [dupError, setDupError] = useState("");

  const unresolvedAlarms = inv.alarms.filter((a) => !a.resolvedAt);
  const criticalAlarms = unresolvedAlarms.filter((a) => a.level === "critical");
  const warningAlarms = unresolvedAlarms.filter((a) => a.level !== "critical");
  const isFlagged = criticalAlarms.length > 0;
  const hasWarnings = warningAlarms.length > 0;
  const suggestedTotal = inv.lineItems.reduce((sum, item) => sum + (item.totalPrice ?? 0), 0);
  const suggestedDate = inv.uploadedAt ? inv.uploadedAt.slice(0, 10) : null;
  const fuelLevyItems = inv.lineItems.filter((item) =>
    /fuel|levy|surcharge|surchg/i.test(item.description ?? "")
  );
  const fuelLevy = fuelLevyItems.reduce((sum, item) => sum + (item.totalPrice ?? 0), 0);
  const externalDestinations = originalFile?.externalStorage || [];
  const externalStorageUnavailable = Boolean(originalFile) && externalDestinations.length === 0;
  const externalStorageNotice = externalStorageMessage || (externalStorageUnavailable ? "This invoice has not been exported to external storage." : "");

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["invoices", businessId] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-summary", businessId] });
  };

  const invoiceMutation = useMutation({
    mutationFn: (updates: InvoiceCorrection) => updateInvoiceFields(inv.id, updates, businessId),
    onSuccess: invalidate,
  });

  const lineMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: LineCorrection }) => updateInvoiceLineFields(id, updates, businessId),
    onSuccess: invalidate,
  });

  const recheckMutation = useMutation({
    mutationFn: () => recheckInvoice(inv.id, businessId),
    onSuccess: (result) => {
      const warnings = result.warnings || [];
      setRecheckMessage(result.success
        ? warnings.length > 0 ? `Invoice is complete. Warnings: ${warnings.join(" ")}` : "Invoice is complete."
        : `Still missing: ${(result.missing || []).join(" ")}`);
      invalidate();
    },
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: () => deleteInvoice(inv.id, businessId),
    onSuccess: () => { setDeleteTarget(null); invalidate(); onClose(); },
    onError: (e) => setDeleteError(e instanceof Error ? e.message : "Could not delete invoice."),
  });

  const dismissDuplicateMutation = useMutation({
    mutationFn: () => dismissDuplicateFile(inv.id, businessId),
    onSuccess: () => { setDupError(""); setReviewDupOpen(false); invalidate(); },
    onError: (e) => setDupError(e instanceof Error ? e.message : "Failed to dismiss duplicate."),
  });

  const resolveDuplicateMutation = useMutation({
    mutationFn: (action: "delete" | "overwrite") => resolveDuplicate(inv.id, action, businessId),
    onSuccess: () => { setDupError(""); setReviewDupOpen(false); invalidate(); },
    onError: (e) => setDupError(e instanceof Error ? e.message : "Failed to resolve duplicate."),
  });

  const deleteLineMutation = useMutation({
    mutationFn: (id: string) => deleteInvoiceLine(id, businessId),
    onSuccess: () => { setDeleteTarget(null); invalidate(); },
    onError: (e) => setDeleteError(e instanceof Error ? e.message : "Could not delete line."),
  });

  const createLineMutation = useMutation({
    mutationFn: (form: LineForm) => createInvoiceLine({
      invoiceId: inv.id, productCode: form.productCode, description: form.description,
      quantity: form.quantity, unit: form.unit, unitPrice: form.unitPrice,
      totalPrice: form.totalPrice, category: form.category,
    }, businessId),
    onSuccess: () => { setAddingLine(false); setLineCreateError(""); invalidate(); },
    onError: (e) => setLineCreateError(e instanceof Error ? e.message : "Could not add invoice line."),
  });

  function confirmDelete(target: DeleteTarget) { setDeleteError(""); setDeleteTarget(target); }
  function handleDeleteConfirmed() {
    if (!deleteTarget) return;
    if (deleteTarget.type === "invoice") deleteInvoiceMutation.mutate();
    else deleteLineMutation.mutate(deleteTarget.id);
  }
  function handleAddLine(form: LineForm) {
    if (!form.description.trim()) { setLineCreateError("Description is required."); return; }
    createLineMutation.mutate(form);
  }

  async function loadOriginalFile() {
    if (originalFile) return originalFile;
    const file = await fetchOriginalInvoiceFile(inv.id, businessId);
    setOriginalFile(file);
    return file;
  }

  useEffect(() => {
    let cancelled = false;
    setPreviewFile(null);
    setOriginalFile(null);
    setPreviewError("");
    setExternalStorageMessage("");
    setExternalMenuOpen(false);
    setPreviewLoading(true);
    fetchOriginalInvoiceFile(inv.id, businessId)
      .then((file) => {
        if (cancelled) return;
        setOriginalFile(file);
        setPreviewFile(file);
      })
      .catch((e) => {
        if (!cancelled) setPreviewError(e instanceof Error ? e.message : "Could not load the original invoice file.");
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [inv.id, businessId]);

  async function openExternalStorage() {
    setExternalStorageMessage("");
    setExternalLoading(true);
    try {
      const file = await loadOriginalFile();
      const destinations = file.externalStorage || [];
      if (destinations.length === 0) {
        setExternalStorageMessage("This invoice has not been exported to external storage.");
        return;
      }
      if (destinations.length === 1) {
        if (!destinations[0].url) {
          setExternalStorageMessage(`External path: ${destinations[0].path}`);
          return;
        }
        window.open(destinations[0].url, "_blank", "noopener,noreferrer"); return;
      }
      setExternalMenuOpen((o) => !o);
    } catch (e) {
      setExternalStorageMessage(e instanceof Error ? e.message : "Could not load external storage links.");
    } finally { setExternalLoading(false); }
  }

  return (
    <>
      {deleteTarget && (
        <DeleteConfirmModal
          target={deleteTarget}
          isDeleting={deleteInvoiceMutation.isPending || deleteLineMutation.isPending}
          error={deleteError}
          showExternalStorageWarning={deleteTarget.type === "invoice" && deletesExternalOnInvoiceDelete}
          onCancel={() => setDeleteTarget(null)}
          onDelete={handleDeleteConfirmed}
        />
      )}
      {exportModalOpen && <StorageExportModal invoiceId={inv.id} businessId={businessId} onClose={() => setExportModalOpen(false)} />}

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        style={{ backgroundColor: "rgba(0,0,0,0.5)", opacity: visible ? 1 : 0, transition: "opacity 0.2s ease" }}
        onClick={onClose}
      />

      {/* Single window */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none">
        <div
          className="pointer-events-auto flex w-full h-full max-w-[1400px] max-h-[920px] flex-col overflow-hidden rounded-2xl"
          style={{
            backgroundColor: SURFACE,
            border: `1px solid ${LINE}`,
            boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
            opacity: visible ? 1 : 0,
            transform: visible ? "scale(1) translateY(0)" : "scale(0.98) translateY(10px)",
            transition: "opacity 0.22s ease, transform 0.28s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 flex-shrink-0 min-w-0" style={{ borderBottom: `1px solid ${NAVY}14` }}>
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <span className="text-[10px] font-bold uppercase tracking-widest flex-shrink-0" style={{ color: NAVY, opacity: 0.4 }}>Invoice</span>
            <h2 className="text-sm font-extrabold truncate" style={{ color: NAVY }}>{inv.invoiceNumber || "—"}</h2>
            <span className="text-xs flex-shrink-0" style={{ color: NAVY, opacity: 0.4 }}>·</span>
            <button
              type="button"
              className="text-xs truncate hover:underline"
              style={{ color: NAVY, opacity: 0.55 }}
              onClick={() => { onClose(); navigate("/suppliers", { state: { supplierId: inv.supplier.id } }); }}
            >{inv.supplier.name}</button>
            <span className="text-xs flex-shrink-0" style={{ color: NAVY, opacity: 0.4 }}>·</span>
            <span className="text-xs flex-shrink-0" style={{ color: NAVY, opacity: 0.4 }}>{inv.supplier.category}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 flex items-center justify-center"
            style={{ color: NAVY, opacity: 0.4 }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body: invoice preview + details */}
        <div className="flex-1 min-h-0 flex">
          {/* Left — invoice preview */}
          <div className="hidden lg:flex flex-col flex-shrink-0" style={{ width: "44%", borderRight: `1px solid ${LINE}`, backgroundColor: RAISED }}>
            <div className="flex items-center justify-between gap-3 px-4 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${LINE}` }}>
              <div className="min-w-0">
                <p className="text-xs font-bold truncate" style={{ color: NAVY }}>{previewFile?.fileName || inv.pdfFileName || "Original invoice"}</p>
                {previewFile && (
                  <p className="text-[11px] mt-0.5" style={{ color: NAVY, opacity: 0.45 }}>Private preview · expires in {Math.round(previewFile.expiresInSeconds / 60)} min</p>
                )}
              </div>
              {previewFile && (
                <a href={previewFile.downloadUrl || previewFile.signedUrl} className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: ORANGE }}>
                  <Download size={13} />
                  Download
                </a>
              )}
            </div>
            <div className="flex-1 min-h-0 flex items-center justify-center p-4" style={{ backgroundColor: "#efe9df" }}>
              {previewLoading ? (
                <Loader2 size={22} className="animate-spin" style={{ color: NAVY, opacity: 0.4 }} />
              ) : previewError ? (
                <div className="text-center">
                  <FileText size={32} style={{ color: NAVY, opacity: 0.25 }} className="mx-auto" />
                  <p className="text-xs mt-2" style={{ color: "#b91c1c" }}>{previewError}</p>
                </div>
              ) : previewFile ? (
                previewFile.mimeType.startsWith("image/") ? (
                  <img src={previewFile.previewUrl || previewFile.signedUrl} alt={previewFile.fileName} className="max-w-full max-h-full object-contain rounded-md" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }} />
                ) : previewFile.mimeType === "application/pdf" ? (
                  <iframe title={previewFile.fileName} src={previewFile.previewUrl || previewFile.signedUrl} className="w-full h-full border-0 rounded-md" />
                ) : (
                  <div className="text-center">
                    <FileText size={32} style={{ color: NAVY, opacity: 0.25 }} className="mx-auto" />
                    <p className="text-sm font-semibold mt-2" style={{ color: NAVY }}>Preview not available.</p>
                    <a href={previewFile.downloadUrl || previewFile.signedUrl} target="_blank" rel="noreferrer" className="mt-3 inline-block px-4 py-2 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: ORANGE }}>Open file</a>
                  </div>
                )
              ) : (
                <p className="text-xs" style={{ color: NAVY, opacity: 0.4 }}>No file</p>
              )}
            </div>
          </div>

          {/* Right — details & line items */}
          <div className="flex-1 min-w-0 overflow-y-auto p-5 space-y-4">

          {/* Invoice details + line items, stacked */}
          <div className="space-y-3">
            <div className="rounded-lg p-4 space-y-4" style={{ border: `1px solid ${LINE}`, backgroundColor: SURFACE }}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.45 }}>Original invoice file</p>
                  <p className="text-xs mt-1 truncate" style={{ color: NAVY }}>{inv.pdfFileName || "Stored private file"}</p>
                  {externalStorageNotice && <p className="text-xs mt-1" style={{ color: UP }}>{externalStorageNotice}</p>}
                </div>
                <div className="relative flex flex-wrap items-center justify-end gap-2">
                  {(inv.hasDuplicateFile || inv.status === "duplicate") && (
                    <button type="button" onClick={() => setReviewDupOpen((p) => !p)}
                      className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-all"
                      style={{ backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #c2410c44" }}>
                      <AlertTriangle size={13} />
                      Review Duplication
                    </button>
                  )}
                  <button type="button" onClick={openExternalStorage} disabled={externalLoading || externalStorageUnavailable}
                    className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-all disabled:opacity-50"
                    style={{ backgroundColor: SURFACE, color: NAVY, border: `1.5px solid ${LINE}` }}>
                    {externalLoading ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
                    {externalLoading ? "Loading..." : "View in external storage"}
                    {(originalFile?.externalStorage?.length || 0) > 1 && <ChevronDown size={12} />}
                  </button>
                  {externalMenuOpen && originalFile?.externalStorage && originalFile.externalStorage.length > 1 && (
                    <div className="absolute right-0 top-full z-20 mt-2 w-72 overflow-hidden rounded-lg shadow-lg" style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}` }}>
                      {originalFile.externalStorage.map((dest, idx) => (
                        <a key={`${dest.provider}-${dest.path}-${idx}`} href={dest.url || undefined} target="_blank" rel="noreferrer"
                          onClick={(e) => { if (!dest.url) e.preventDefault(); setExternalMenuOpen(false); }}
                          className="block px-3 py-2.5 transition-colors"
                          style={{ borderTop: idx > 0 ? `1px solid ${NAVY}0d` : undefined, color: NAVY }}>
                          <span className="block text-xs font-bold capitalize">{dest.provider}</span>
                          <span className="block truncate text-[11px] opacity-60">{dest.path}</span>
                        </a>
                      ))}
                    </div>
                  )}
                  <button type="button" onClick={() => setExportModalOpen(true)}
                    className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-all"
                    style={{ backgroundColor: SURFACE, color: NAVY, border: `1.5px solid ${LINE}` }}>
                    <CloudUpload size={13} />
                    Upload to cloud
                  </button>
                  {previewFile && (
                    <a href={previewFile.previewUrl || previewFile.signedUrl} target="_blank" rel="noreferrer"
                      className="lg:hidden inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold transition-all"
                      style={{ backgroundColor: SURFACE, color: NAVY, border: `1.5px solid ${LINE}` }}>
                      <Eye size={13} />
                      View invoice
                    </a>
                  )}
                </div>
              </div>

              {(inv.hasDuplicateFile || inv.status === "duplicate") && reviewDupOpen && (
                <div className="rounded-lg px-3 py-3 space-y-3" style={{ backgroundColor: "#fff7ed", border: "1px solid #c2410c33" }}>
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" style={{ color: "#c2410c" }} />
                    <div>
                      <p className="text-xs font-bold" style={{ color: "#c2410c" }}>Duplicate invoice detected</p>
                      <p className="text-xs mt-0.5" style={{ color: "#9a3412" }}>
                        An invoice with number <span className="font-bold tabular-nums">{inv.invoiceNumber || "—"}</span> already exists.
                        Find the original, then decide which to keep.
                      </p>
                    </div>
                  </div>
                  {dupError && (
                    <p className="text-xs font-semibold" style={{ color: "#dc2626" }}>{dupError}</p>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {inv.invoiceNumber && onSearch && (
                      <button type="button" onClick={() => { onSearch(inv.invoiceNumber!); onClose(); }}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all"
                        style={{ backgroundColor: SURFACE, color: NAVY, border: `1.5px solid ${LINE}` }}>
                        <ExternalLink size={11} />
                        Find original invoice
                      </button>
                    )}
                    {inv.status === "duplicate" ? (
                      <>
                        <button
                          type="button"
                          disabled={resolveDuplicateMutation.isPending}
                          onClick={() => resolveDuplicateMutation.mutate("overwrite")}
                          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all disabled:opacity-50"
                          style={{ backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #c2410c44" }}>
                          {resolveDuplicateMutation.isPending ? "Resolving..." : "Overwrite original"}
                        </button>
                        <button
                          type="button"
                          disabled={resolveDuplicateMutation.isPending}
                          onClick={() => resolveDuplicateMutation.mutate("delete")}
                          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all disabled:opacity-50"
                          style={{ backgroundColor: SURFACE, color: UP, border: `1.5px solid ${UP}33` }}>
                          <Trash2 size={11} />
                          Delete this duplicate
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={dismissDuplicateMutation.isPending}
                        onClick={() => dismissDuplicateMutation.mutate()}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all disabled:opacity-50"
                        style={{ backgroundColor: ORANGE, color: "white" }}>
                        {dismissDuplicateMutation.isPending ? "Resolving..." : "Keep original, dismiss duplicate"}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {(criticalAlarms.length > 0 || warningAlarms.length > 0) && (
                <div className="space-y-2">
                  {criticalAlarms.map((alarm) => (
                    <div key={alarm.id} className="flex items-start gap-2.5 rounded-lg px-3 py-2 text-xs" style={{ color: UP, backgroundColor: UP_WEAK, border: `1px solid ${UP}33` }}>
                      <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" style={{ color: UP }} />
                      {alarm.message}
                    </div>
                  ))}
                  {warningAlarms.map((alarm) => (
                    <div key={alarm.id} className="flex items-start gap-2.5 rounded-lg px-3 py-2 text-xs" style={{ color: WARN, backgroundColor: WARN_WEAK, border: `1px solid ${WARN}33` }}>
                      <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" style={{ color: WARN }} />
                      {alarm.message}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ borderTop: `1px solid ${LINE}`, paddingTop: 14 }}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="text-[10px] font-bold uppercase tracking-widest"
                  style={{ color: NAVY, opacity: 0.45 }}>
                  Invoice details
                </p>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => recheckMutation.mutate()}
                    disabled={recheckMutation.isPending || invoiceMutation.isPending}
                    className="inline-flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-xs font-bold transition-all disabled:opacity-50"
                    style={{ backgroundColor: SURFACE, color: NAVY, border: `1.5px solid ${LINE}` }}>
                    <CheckCircle size={13} />
                    {recheckMutation.isPending ? "Checking..." : invoiceMutation.isPending ? "Saving…" : "Finish"}
                  </button>
                  <button type="button"
                    onClick={() => confirmDelete({ type: "invoice", id: inv.id, title: "Delete invoice?", body: `Delete invoice ${inv.invoiceNumber || "without a number"} and all of its line items.` })}
                    className="inline-flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-xs font-bold transition-all"
                    style={{ backgroundColor: SURFACE, color: UP, border: `1px solid ${UP}33` }}>
                    <Trash2 size={13} />
                    Delete
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-3">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: NAVY, opacity: 0.48 }}>Number</span>
                  <EditableCell value={inv.invoiceNumber} onSave={(v) => invoiceMutation.mutate({ invoiceNumber: v })} />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: NAVY, opacity: 0.48 }}>Date</span>
                  <EditableCell type="date" value={inv.invoiceDate} onSave={(v) => invoiceMutation.mutate({ invoiceDate: v })} />
                  {!inv.invoiceDate && suggestedDate && (
                    <button type="button" onClick={() => invoiceMutation.mutate({ invoiceDate: suggestedDate })}
                      className="self-start text-[10px] font-bold hover:underline" style={{ color: ORANGE }}>
                      Use upload date · {suggestedDate}
                    </button>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: NAVY, opacity: 0.48 }}>Subtotal</span>
                  <div className="rounded-md px-2.5 py-1.5 text-xs font-medium" style={{ border: `1px solid ${LINE}`, backgroundColor: SURFACE, color: NAVY }}>{inv.subtotal != null ? displayCurrency(inv.subtotal) : "—"}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide truncate" style={{ color: NAVY, opacity: 0.48 }}>{fuelLevyItems.length === 1 ? (fuelLevyItems[0].description ?? "Fuel Levy") : "Fuel Levy"}</span>
                  <div className="rounded-md px-2.5 py-1.5 text-xs font-medium" style={{ border: `1px solid ${LINE}`, backgroundColor: SURFACE, color: NAVY }}>{fuelLevyItems.length > 0 ? displayCurrency(fuelLevy) : "—"}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: NAVY, opacity: 0.48 }}>GST</span>
                  <div className="rounded-md px-2.5 py-1.5 text-xs font-medium" style={{ border: `1px solid ${LINE}`, backgroundColor: SURFACE, color: NAVY }}>{inv.taxAmount != null ? displayCurrency(inv.taxAmount) : "—"}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: NAVY, opacity: 0.58 }}>Total</span>
                  <EditableCell type="number" align="right" value={inv.totalAmount} onSave={(v) => invoiceMutation.mutate({ totalAmount: v })} />
                  {Math.abs(suggestedTotal - (inv.totalAmount ?? 0)) > 0.005 && (
                    <button type="button" onClick={() => invoiceMutation.mutate({ totalAmount: formatNumber(suggestedTotal) })}
                      className="self-end text-[10px] font-bold hover:underline" style={{ color: ORANGE }}>
                      Use line total · {displayCurrency(suggestedTotal)}
                    </button>
                  )}
                </div>
              </div>
              {recheckMessage && (
                <div className="mt-3 rounded-md px-3 py-2 text-xs font-medium" style={{ color: recheckMessage.includes("Warnings:") ? WARN : recheckMessage.startsWith("Invoice") ? DOWN : UP, backgroundColor: SURFACE }}>
                  {recheckMessage}
                </div>
              )}
              </div>
            </div>

            {/* Line items */}
            <div className="min-w-0 rounded-lg p-3" style={{ border: `1px solid ${LINE}`, backgroundColor: SURFACE }}>
              {inv.lineItems.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.45 }}>
                      Line Items
                    </p>
                    <button type="button" onClick={() => { setLineCreateError(""); setAddingLine(true); }}
                      className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold"
                      style={{ backgroundColor: SURFACE, color: NAVY, border: `1.5px solid ${LINE}` }}>
                      <Plus size={12} />
                      Add line
                    </button>
                  </div>
                  <div className="rounded-lg overflow-x-auto" style={{ border: `1px solid ${LINE}`, backgroundColor: SURFACE }}>
                    <table className="w-full text-sm min-w-[600px]" style={{ tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: 32 }} />
                      <col style={{ width: 100 }} />
                      <col />
                      <col style={{ width: 72 }} />
                      <col style={{ width: 88 }} />
                      <col style={{ width: 90 }} />
                      <col style={{ width: 90 }} />
                      <col style={{ width: 44 }} />
                    </colgroup>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${LINE}`, backgroundColor: RAISED }}>
                        <th className="px-3 py-2.5 text-xs font-semibold text-center" style={{ color: NAVY, opacity: 0.35 }}>#</th>
                        <th className="px-3 py-2.5 text-xs font-semibold text-center" style={{ color: NAVY, opacity: 0.5 }}>Product Code</th>
                        <th className="px-3 py-2.5 text-xs font-semibold text-left" style={{ color: NAVY, opacity: 0.5 }}>Description</th>
                        <th className="px-3 py-2.5 text-xs font-semibold text-center" style={{ color: NAVY, opacity: 0.5 }}>Qty</th>
                        <th className="px-3 py-2.5 text-xs font-semibold text-center" style={{ color: NAVY, opacity: 0.5 }}>Unit</th>
                        <th className="px-3 py-2.5 text-xs font-semibold text-center" style={{ color: NAVY, opacity: 0.5 }}>Unit Price</th>
                        <th className="px-3 py-2.5 text-xs font-semibold text-center" style={{ color: NAVY, opacity: 0.5 }}>Total</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {inv.lineItems.map((item, idx) => {
                        const highlighted = Boolean(highlightProductCode && item.productCode === highlightProductCode);
                        return (
                        <tr key={item.id} style={{ borderTop: `1px solid ${LINE}`, backgroundColor: highlighted ? ACCENT_WEAK : undefined }}>
                          <td className="px-3 py-2 text-center text-xs tabular-nums select-none" style={{ color: NAVY, opacity: 0.3 }}>{idx + 1}</td>
                          <td className="px-3 py-2"><EditableCell align="center" value={item.productCode || ""} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { productCode: v } })} /></td>
                          <td className="px-3 py-2"><EditableCell value={item.description} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { description: v } })} /></td>
                          <td className="px-3 py-2"><EditableCell align="center" value={item.quantity} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { quantity: v } })} /></td>
                          <td className="px-3 py-2"><EditableCell align="center" value={item.unit} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { unit: v } })} /></td>
                          <td className="px-3 py-2"><EditableCell type="number" align="center" value={item.unitPrice} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { unitPrice: v } })} /></td>
                          <td className="px-3 py-2 font-bold"><EditableCell type="number" align="center" value={item.totalPrice} onSave={(v) => lineMutation.mutate({ id: item.id, updates: { totalPrice: v } })} /></td>
                          <td className="px-3 py-2 text-center">
                            <button type="button"
                              onClick={() => confirmDelete({ type: "line", id: item.id, title: "Delete invoice line?", body: `Delete ${item.description || "this line item"} from this invoice.` })}
                              className="inline-flex items-center justify-center w-7 h-7 rounded-md transition-all opacity-30 hover:opacity-100"
                              style={{ color: "#dc2626" }}>
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {inv.lineItems.length === 0 && (
              <button type="button" onClick={() => { setLineCreateError(""); setAddingLine(true); }}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold"
                style={{ backgroundColor: SURFACE, color: NAVY, border: `1.5px solid ${LINE}` }}>
                <Plus size={12} />
                Add invoice line
              </button>
            )}
            {addingLine && (
              <AddLineForm
                defaultCategory={inv.supplier.category}
                isSaving={createLineMutation.isPending}
                categories={categories}
                onCancel={() => { setAddingLine(false); setLineCreateError(""); }}
                onSubmit={handleAddLine}
              />
            )}
            {lineCreateError && (
              <p className="text-xs rounded-md px-3 py-2 mt-2" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)" }}>{lineCreateError}</p>
            )}
          </div>

          </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function paginationItems(current: number, total: number): Array<number | "ellipsis"> {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

  const items = new Set<number>([1, total, current - 1, current, current + 1]);
  if (current <= 4) {
    [2, 3, 4, 5].forEach((page) => items.add(page));
  }
  if (current >= total - 3) {
    [total - 4, total - 3, total - 2, total - 1].forEach((page) => items.add(page));
  }

  const pages = Array.from(items).filter((page) => page >= 1 && page <= total).sort((a, b) => a - b);
  return pages.reduce<Array<number | "ellipsis">>((acc, page) => {
    const previous = acc[acc.length - 1];
    if (typeof previous === "number" && page - previous > 1) acc.push("ellipsis");
    acc.push(page);
    return acc;
  }, []);
}

export default function InvoiceListPage() {
  const { activeBusiness } = useBusiness();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState<DateRange>("week");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [sortRule, setSortRule] = useState<SortRule>("uploaded_newest");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [scanOpen, setScanOpen] = useState(false);
  const dateFilterRef = useRef<HTMLDivElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [lastVisitTime, setLastVisitTime] = useState<number | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [downloadError, setDownloadError] = useState("");
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState("");

  useEffect(() => {
    if (!activeBusiness?.id) return;
    const key = `iscan_invoice_page_last_visit_${activeBusiness.id}`;
    const stored = localStorage.getItem(key);
    setLastVisitTime(stored ? Number(stored) : null);
    localStorage.setItem(key, String(Date.now()));
  }, [activeBusiness?.id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const incomingRange = params.get("dateRange");
    const start = params.get("start") || "";
    const end = params.get("end") || "";
    if (incomingRange === "custom" && (start || end)) {
      setDateRange("custom");
      setCustomStart(start);
      setCustomEnd(end);
    }
  }, [location.search]);

  useEffect(() => {
    const state = location.state as { supplierId?: string } | null;
    if (state?.supplierId) setSupplierFilter(state.supplierId);
    if (state?.supplierId) {
      // Clear the state so navigating back doesn't re-apply the filter
      window.history.replaceState({}, "");
    }
  }, []);

  useEffect(() => {
    if (!datePickerOpen && !filterPanelOpen) return;

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (dateFilterRef.current?.contains(target) || filterPanelRef.current?.contains(target)) return;
      setDatePickerOpen(false);
      setFilterPanelOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setDatePickerOpen(false);
        setFilterPanelOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [datePickerOpen, filterPanelOpen]);

  useEffect(() => {
    setPage(1);
  }, [activeBusiness?.id, dateRange, customStart, customEnd, supplierFilter, sortRule, searchQuery]);

  const { data: businessDetails } = useQuery({
    queryKey: ["business-details", activeBusiness?.id],
    queryFn: () => fetchBusinessDetails(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
    staleTime: 5 * 60_000,
  });
  const categories = getCategoriesForIndustry(businessDetails?.industry);

  const dateQueryParams = invoiceDateQueryParams(dateRange, customStart, customEnd);
  const searchTerm = searchQuery.trim();
  const invoiceQueryParams = {
    page,
    pageSize: INVOICE_PAGE_SIZE,
    ...dateQueryParams,
    supplierId: supplierFilter,
    search: searchTerm || undefined,
    sort: sortRule,
  };

  const {
    data: invoicePage,
    error: invoicesError,
    isFetching: invoicesFetching,
    isLoading: invoicesLoading,
  } = useQuery<PaginatedInvoicesResponse>({
    queryKey: ["invoices", activeBusiness?.id, invoiceQueryParams],
    queryFn: () => fetchProcessedInvoices(activeBusiness?.id, invoiceQueryParams),
    enabled: Boolean(activeBusiness),
    placeholderData: (previous) => previous,
  });
  const { data: storageIntegrationsData } = useQuery<StorageIntegration[]>({
    queryKey: ["storage-integrations", activeBusiness?.id],
    queryFn: () => fetchStorageIntegrations(activeBusiness?.id),
    enabled: Boolean(activeBusiness?.id),
  });
  const storageIntegrations: StorageIntegration[] = storageIntegrationsData ?? [];
  const deletesExternalOnInvoiceDelete = storageIntegrations.some((integration) =>
    integration.status === "connected" && integration.deleteOnInvoiceDelete
  );

  // Auto-refresh when a new invoice is inserted or updated for this business
  useEffect(() => {
    if (!activeBusiness?.id) return;
    const channel = supabase
      .channel(`invoices:${activeBusiness.id}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "invoices",
        filter: `business_id=eq.${activeBusiness.id}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ["invoices", activeBusiness.id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeBusiness?.id, queryClient]);

  const { data: suppliers } = useQuery<Supplier[]>({
    queryKey: ["suppliers", activeBusiness?.id],
    queryFn: () => fetchSuppliers(activeBusiness?.id),
    enabled: Boolean(activeBusiness),
  });

  const list: Invoice[] = invoicePage?.invoices ?? [];
  const totalInvoices = invoicePage?.total ?? 0;
  const totalPages = invoicePage?.totalPages ?? 1;
  const safePage = Math.min(invoicePage?.page ?? page, totalPages);

  useEffect(() => {
    if (invoicePage && page > invoicePage.totalPages) setPage(invoicePage.totalPages);
  }, [invoicePage, page]);

  const supplierOptions: Supplier[] = (suppliers && suppliers.length > 0
    ? suppliers
    : Array.from(new Map(list.map((invoice) => [invoice.supplier.id, invoice.supplier])).values())
  ).sort((a: Supplier, b: Supplier) => a.name.localeCompare(b.name));

  const filtered = list;
  const panelInvoice = selectedInvoiceId ? list.find((i) => i.id === selectedInvoiceId) ?? null : null;
  const currentRangeLabel = rangeLabel(safePage, INVOICE_PAGE_SIZE, totalInvoices);

  // Selecting invoices carries across pages; the header checkbox reflects only
  // the invoices currently visible on this page.
  const pageIds = filtered.map((invoice) => invoice.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    setSelectedIds(new Set());
    setDownloadError("");
    setBulkDeleteOpen(false);
    setBulkDeleteError("");
  }, [activeBusiness?.id]);

  const downloadSelectedMutation = useMutation({
    mutationFn: () => downloadInvoicesBatch(Array.from(selectedIds), activeBusiness?.id),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setSelectedIds(new Set());
      setDownloadError("");
    },
    onError: (error) => setDownloadError(error instanceof Error ? error.message : "Could not download the selected invoices."),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selectedIds);
      const results = await Promise.allSettled(ids.map((id) => deleteInvoice(id, activeBusiness?.id)));
      const failed = results.filter((result) => result.status === "rejected").length;
      if (failed > 0) throw new Error(`${failed} of ${ids.length} invoice${ids.length > 1 ? "s" : ""} could not be deleted.`);
    },
    onSuccess: () => {
      setBulkDeleteOpen(false);
      setBulkDeleteError("");
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["invoices", activeBusiness?.id] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-summary", activeBusiness?.id] });
    },
    onError: (error) => setBulkDeleteError(error instanceof Error ? error.message : "Could not delete the selected invoices."),
  });

  function toggleSelect(id: string) {
    setDownloadError("");
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllOnPage() {
    setDownloadError("");
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  }

  return (
    <div
      className="flex flex-col h-full"
      style={{ backgroundColor: CANVAS, fontFamily: "var(--font-ui)" }}
    >
      {scanOpen && <ScanModal onClose={() => setScanOpen(false)} />}

      {bulkDeleteOpen && (
        <DeleteConfirmModal
          target={{
            type: "invoice",
            id: "bulk",
            title: `Delete ${selectedIds.size} invoice${selectedIds.size > 1 ? "s" : ""}?`,
            body: `You're about to permanently delete ${selectedIds.size} selected invoice${selectedIds.size > 1 ? "s" : ""} along with their line items.`,
          }}
          isDeleting={bulkDeleteMutation.isPending}
          error={bulkDeleteError}
          showExternalStorageWarning={deletesExternalOnInvoiceDelete}
          onCancel={() => { if (!bulkDeleteMutation.isPending) { setBulkDeleteOpen(false); setBulkDeleteError(""); } }}
          onDelete={() => bulkDeleteMutation.mutate()}
        />
      )}

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {invoicesError && (
          <div
            className="px-4 py-3 rounded-lg text-xs"
            style={{
              color: "#991b1b",
              backgroundColor: "rgba(220,38,38,0.06)",
              border: "1px solid rgba(220,38,38,0.2)",
            }}
          >
            Could not load invoices: {(invoicesError as Error).message}
          </div>
        )}

        {/* Table */}
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: SURFACE, boxShadow: SHADOW, border: `1px solid ${LINE}` }}>
          <div className="px-6 pb-4 pt-6">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <h1 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>Invoices</h1>
                <p className="text-xs mt-1" style={{ color: NAVY, opacity: 0.55 }}>
                  {currentRangeLabel}
                  {invoicesFetching && <span style={{ opacity: 0.6 }}> · Updating…</span>}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setScanOpen(true)}
                className="inline-flex h-10 items-center gap-2 rounded-lg px-4 text-xs font-bold text-white transition-all"
                style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
              >
                <ScanLine size={13} />
                Scan Invoice
              </button>
            </div>
          </div>

          <div className="px-5 pb-4 pt-2" style={{ borderBottom: `1px solid ${LINE}` }}>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="relative w-full lg:max-w-[360px]">
                <Search
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: NAVY, opacity: 0.45 }}
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search invoice number"
                  className="h-10 w-full rounded-md pl-10 pr-8 text-xs outline-none transition-all"
                  style={{
                    border: `1px solid ${searchQuery ? NAVY + "55" : "transparent"}`,
                    backgroundColor: "#f7f7f3",
                    color: NAVY,
                  }}
                  onFocus={(e) => (e.target.style.border = `1px solid ${NAVY}55`)}
                  onBlur={(e) => (e.target.style.border = `1px solid ${searchQuery ? NAVY + "55" : "transparent"}`)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setPage(1);
                      searchRef.current?.focus();
                    }}
                    className="absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full transition-colors hover:bg-black/5"
                    style={{ color: NAVY, opacity: 0.45 }}
                    aria-label="Clear invoice search"
                  >
                    <X size={11} />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                {selectedIds.size > 0 && (
                  <div
                    className="flex h-10 items-center gap-2 rounded-md px-3"
                    style={{ backgroundColor: ACCENT_WEAK, border: `1px solid ${ACCENT_LINE}` }}
                  >
                    <span className="text-xs font-bold" style={{ color: NAVY }}>
                      {selectedIds.size} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => { setSelectedIds(new Set()); setDownloadError(""); }}
                      className="text-xs font-semibold transition-colors"
                      style={{ color: MUTED }}
                    >
                      Clear
                    </button>
                  </div>
                )}
                {downloadError && (
                  <span className="text-xs font-semibold" style={{ color: UP }}>{downloadError}</span>
                )}

                <div ref={filterPanelRef} className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setFilterPanelOpen((open) => !open);
                      setDatePickerOpen(false);
                    }}
                    className="inline-flex h-10 min-w-[116px] items-center justify-between gap-2 rounded-md px-3 text-xs font-bold transition-colors"
                    style={{ color: NAVY, backgroundColor: "#f7f7f3", border: "1px solid transparent" }}
                  >
                    <Filter size={14} />
                    Filter by
                    <ChevronDown size={14} style={{ opacity: 0.55 }} />
                  </button>
                  {filterPanelOpen && (
                    <div
                      className="absolute right-0 top-full z-30 mt-2 w-64 rounded-lg p-3"
                      style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}
                    >
                      <label className="block">
                        <span className="text-xs font-semibold" style={{ color: NAVY, opacity: 0.5 }}>Supplier</span>
                        <select
                          value={supplierFilter}
                          onChange={(event) => {
                            setSupplierFilter(event.target.value);
                            setPage(1);
                          }}
                          className="mt-2 w-full rounded-lg px-3 py-2 text-xs font-semibold outline-none"
                          style={{ border: `1.5px solid ${LINE}`, color: NAVY, backgroundColor: SURFACE }}
                        >
                          <option value="all">All suppliers</option>
                          {supplierOptions.map((supplier) => (
                            <option key={supplier.id} value={supplier.id}>
                              {supplier.name}
                            </option>
                          ))}
                        </select>
                      </label>

                      {supplierFilter !== "all" && (
                        <button
                          type="button"
                          onClick={() => {
                            setSupplierFilter("all");
                            setPage(1);
                          }}
                          className="mt-3 w-full rounded-lg px-3 py-2 text-xs font-bold transition-all"
                          style={{ color: NAVY, backgroundColor: `${NAVY}0d`, border: `1px solid ${NAVY}1a` }}
                        >
                          Clear supplier
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <label
                  className="relative inline-flex h-10 min-w-[110px] cursor-pointer items-center justify-between gap-2 rounded-md px-3 text-xs font-bold"
                  style={{ color: NAVY, backgroundColor: "#f7f7f3", border: "1px solid transparent" }}
                >
                  <ArrowUpDown size={14} />
                  <span>Sort by</span>
                  <ChevronDown size={14} style={{ opacity: 0.55 }} />
                  <select
                    aria-label="Sort invoices"
                    value={sortRule}
                    onChange={(event) => {
                      setSortRule(event.target.value as SortRule);
                      setPage(1);
                    }}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>

                <div ref={dateFilterRef} className="relative flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setDatePickerOpen((open) => !open);
                      setFilterPanelOpen(false);
                    }}
                    className="inline-flex h-10 min-w-[132px] items-center justify-between gap-2 rounded-md px-3 text-xs font-bold transition-colors"
                    style={{ color: NAVY, backgroundColor: "#f7f7f3", border: "1px solid transparent" }}
                  >
                    <Calendar size={14} />
                    Select dates
                    <ChevronDown size={14} />
                  </button>

                  {datePickerOpen && (
                    <div
                      className="absolute right-0 top-full z-30 mt-2 w-[540px] max-w-[calc(100vw-3rem)] rounded-lg p-3"
                      style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW }}
                    >
                      <div className="grid grid-cols-5 gap-2">
                        {DATE_FILTERS.map((option) => {
                          const active = dateRange === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setDateRange(option.value);
                                setPage(1);
                                if (option.value !== "custom") setDatePickerOpen(false);
                              }}
                              className="h-8 rounded-lg text-xs font-bold transition-colors"
                              style={active
                                ? { backgroundColor: ACCENT_WEAK, color: ORANGE }
                                : { color: MUTED, backgroundColor: `${NAVY}08` }
                              }
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>

                      {dateRange === "custom" && (
                        <>
                          <div className="mt-4">
                            <DateRangeCalendar
                              start={customStart}
                              end={customEnd}
                              onChange={({ start, end }) => {
                                setCustomStart(start);
                                setCustomEnd(end);
                                setPage(1);
                              }}
                            />
                          </div>
                          <div className="mt-4 flex justify-end">
                            <button
                              type="button"
                              onClick={() => setDatePickerOpen(false)}
                              className="rounded-lg px-4 py-2 text-xs font-bold text-white"
                              style={{ backgroundColor: ORANGE }}
                            >
                              Done
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => downloadSelectedMutation.mutate()}
                  disabled={selectedIds.size === 0 || downloadSelectedMutation.isPending}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md transition-all disabled:cursor-not-allowed"
                  style={{
                    color: selectedIds.size > 0 ? ORANGE : `${NAVY}55`,
                    backgroundColor: "transparent",
                    border: "1px solid transparent",
                  }}
                  aria-label="Download selected invoices"
                  title="Download selected invoices"
                >
                  {downloadSelectedMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                </button>

                <button
                  type="button"
                  onClick={() => { setBulkDeleteError(""); setBulkDeleteOpen(true); }}
                  disabled={selectedIds.size === 0 || bulkDeleteMutation.isPending}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md transition-all disabled:cursor-not-allowed"
                  style={{
                    color: selectedIds.size > 0 ? "#dc2626" : `${NAVY}55`,
                    backgroundColor: "transparent",
                    border: "1px solid transparent",
                  }}
                  aria-label="Delete selected invoices"
                  title="Delete selected invoices"
                >
                  {bulkDeleteMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div role="table" aria-label="Invoices" className="min-w-[1180px] space-y-2">
              <div
                role="row"
                className="grid items-center gap-4 px-5 py-2.5"
                style={{ gridTemplateColumns: INVOICE_LIST_COLUMNS, borderBottom: `1px solid ${LINE}` }}
              >
                <div role="columnheader" className="flex justify-center">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={(el) => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                    onChange={toggleSelectAllOnPage}
                    disabled={pageIds.length === 0}
                    className="cursor-pointer align-middle"
                    style={{ width: 15, height: 15, accentColor: ORANGE }}
                    aria-label="Select all invoices on this page"
                  />
                </div>
                {["Invoice #", "Supplier", "Uploaded", "Uploaded By", "Invoice Date"].map((h) => (
                  <div key={h} role="columnheader" className="text-sm font-bold whitespace-nowrap" style={{ color: NAVY, opacity: 0.42 }}>{h}</div>
                ))}
                <div role="columnheader" className="text-right text-sm font-bold whitespace-nowrap" style={{ color: NAVY, opacity: 0.42 }}>Tax</div>
                <div role="columnheader" className="text-center text-sm font-bold whitespace-nowrap" style={{ color: NAVY, opacity: 0.42 }}>Total</div>
                <div role="columnheader" />
              </div>

              {invoicesLoading ? (
                <div role="row" className="px-5 py-16 text-center">
                  <Loader2 size={28} className="mx-auto mb-3 animate-spin" style={{ color: NAVY, opacity: 0.35 }} />
                  <p className="text-xs font-medium" style={{ color: NAVY, opacity: 0.45 }}>Loading invoices</p>
                </div>
              ) : filtered.length === 0 ? (
                <div role="row" className="px-5 py-16 text-center">
                  <Receipt size={32} className="mx-auto mb-3" style={{ color: NAVY, opacity: 0.2 }} />
                  <p className="text-xs font-medium" style={{ color: NAVY, opacity: 0.45 }}>No invoices found</p>
                  <p className="text-xs mt-1" style={{ color: NAVY, opacity: 0.3 }}>Try a wider date range or clear search</p>
                </div>
              ) : (
                filtered.map((inv, index) => {
                  const isNew = lastVisitTime != null && inv.uploadedAt != null && new Date(inv.uploadedAt).getTime() > lastVisitTime;
                  return (
                    <InvoiceRow
                      key={inv.id}
                      inv={inv}
                      businessId={activeBusiness?.id}
                      searchQuery={searchTerm}
                      categories={categories}
                      isNew={isNew}
                      rowIndex={index}
                      deletesExternalOnInvoiceDelete={deletesExternalOnInvoiceDelete}
                      selected={selectedIds.has(inv.id)}
                      onToggleSelect={toggleSelect}
                      onSearch={setSearchQuery}
                      onOpen={setSelectedInvoiceId}
                    />
                  );
                })
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 items-center gap-2 px-5 py-4 md:grid-cols-[1fr_auto_1fr]" style={{ borderTop: `1px solid ${LINE}`, backgroundColor: SURFACE }}>
            <p className="text-xs font-semibold" style={{ color: NAVY, opacity: 0.65 }}>
              Page {safePage} of {totalPages}
            </p>
            <div className="flex items-center justify-center gap-1">
              {paginationItems(safePage, totalPages).map((item, index) => (
                item === "ellipsis" ? (
                  <span key={`ellipsis-${index}`} className="px-2 text-xs" style={{ color: MUTED }}>...</span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setPage(item)}
                    disabled={item === safePage || invoicesFetching}
                    className="h-7 min-w-7 rounded-md px-2 text-xs font-semibold transition-colors disabled:cursor-default"
                    style={item === safePage
                      ? { color: ORANGE, backgroundColor: UP_WEAK }
                      : { color: MUTED, backgroundColor: "transparent" }
                    }
                  >
                    {item}
                  </button>
                )
              ))}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={safePage <= 1 || invoicesFetching}
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-bold transition-all disabled:opacity-35"
                style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}
                aria-label="Previous invoice page"
              >
                <ChevronLeft size={14} />
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={safePage >= totalPages || invoicesFetching}
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-bold transition-all disabled:opacity-35"
                style={{ color: NAVY, backgroundColor: SURFACE, border: `1px solid ${LINE}` }}
                aria-label="Next invoice page"
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {panelInvoice && (
        <InvoiceSlidingPanel
          inv={panelInvoice}
          businessId={activeBusiness?.id}
          categories={categories}
          deletesExternalOnInvoiceDelete={deletesExternalOnInvoiceDelete}
          onClose={() => setSelectedInvoiceId(null)}
          onSearch={setSearchQuery}
        />
      )}
    </div>
  );
}
